import React from 'react';
import styles from '../styles/SessionConfig.module.css';
import { SessionConfig as SessionConfigType, defaultSessionConfig } from '../config/realtimeConfig';
import { getAllTools } from '../tools';

type ApiTool = {
  type: 'function';
  name: string;
  description: string;
  parameters: Record<string, any>;
};

type Tool = {
  schema: {
    name: string;
    description: string;
    parameters: Record<string, any>;
  };
  fn: (...args: any[]) => Promise<any>;
};

interface Props {
  config: Partial<SessionConfigType>;
  onChange: (newConfig: Partial<SessionConfigType>) => void;
  disabled?: boolean;
}

export function SessionConfig({ config, onChange, disabled }: Props) {
  const handleChange = (key: string, value: any) => {
    onChange({
      ...config,
      [key]: value
    });
  };

  const renderValue = (key: string, value: any) => {
    // Special handling for tools selection
    if (key === 'tools') {
      const allTools = getAllTools() as Tool[];
      // Convert tools to API format
      const apiTools = allTools.map(tool => ({
        type: 'function',
        name: tool.schema.name,
        description: tool.schema.description,
        parameters: tool.schema.parameters
      }));
      const selectedTools = (value || apiTools) as ApiTool[];
      
      return (
        <div className={styles.toolsList}>
          {allTools.map(tool => (
            <label key={tool.schema.name} className={styles.toolItem}>
              <input
                type="checkbox"
                checked={selectedTools.some((t: ApiTool) => t.name === tool.schema.name)}
                onChange={(e) => {
                  const apiTool = {
                    type: 'function',
                    name: tool.schema.name,
                    description: tool.schema.description,
                    parameters: tool.schema.parameters
                  };
                  const newTools = e.target.checked
                    ? [...selectedTools, apiTool]
                    : selectedTools.filter((t: ApiTool) => t.name !== tool.schema.name);
                  handleChange(key, newTools);
                }}
                className={styles.configCheckbox}
                disabled={disabled}
              />
              <span className={styles.toolName}>{tool.schema.name}</span>
            </label>
          ))}
        </div>
      );
    }

    // Special handling for known types
    if (key === 'voice') {
      return (
        <select
          value={value as string}
          onChange={(e) => handleChange(key, e.target.value)}
          className={styles.configSelect}
          disabled={disabled}
        >
          {['alloy', 'ash', 'ballad', 'coral', 'echo', 'sage', 'shimmer', 'verse'].map(option => (
        <option key={option} value={option}>{option}</option>
          ))}
        </select>
      );
    }

    if (key === 'temperature') {
      return (
        <input
          type="number"
          value={value as number}
          onChange={(e) => handleChange(key, parseFloat(e.target.value))}
          min={0.6}
          max={1.2}
          step={0.1}
          className={styles.configInput}
          disabled={disabled}
        />
      );
    }

    if (Array.isArray(value)) {
      if (key === 'modalities') {
        return (
          <select
            value={value.join(',')}
            onChange={(e) => handleChange(key, e.target.value.split(','))}
            className={styles.configSelect}
            disabled={disabled}
          >
            <option value="text">text</option>
            <option value="audio">audio</option>
            <option value="text,audio">text,audio</option>
          </select>
        );
      }
      return (
        <div className={styles.arrayValue}>
          {value.map((item, index) => (
            <div key={index} className={styles.arrayItem}>
              <div className={styles.arrayItemHeader}>
                <span className={styles.configLabel}>Item {index + 1}</span>
              </div>
              {renderValue(`${key}[${index}]`, item)}
            </div>
          ))}
        </div>
      );
    }

    if (typeof value === 'object' && value !== null) {
      return (
        <div className={styles.objectValue}>
          {Object.entries(value).map(([subKey, subValue]) => (
            <div key={subKey} className={styles.objectField}>
              <label className={styles.configLabel}>{subKey}</label>
              {renderValue(`${key}.${subKey}`, subValue)}
            </div>
          ))}
        </div>
      );
    }

    if (typeof value === 'boolean') {
      return (
        <input
          type="checkbox"
          checked={value}
          onChange={(e) => handleChange(key, e.target.checked)}
          className={styles.configCheckbox}
          disabled={disabled}
        />
      );
    }

    // Default to text input
    return (
      <input
        type="text"
        value={value?.toString() ?? ''}
        onChange={(e) => handleChange(key, e.target.value)}
        className={styles.configInput}
        disabled={disabled}
      />
    );
  };

  // Merge with defaultSessionConfig to show all available options
  const fullConfig = {
    ...defaultSessionConfig,
    ...config
  };

  return (
    <div className={styles.sessionConfig}>
      <div className={styles.configFields}>
        {Object.entries(fullConfig).map(([key, value]) => (
          <div key={key} className={styles.configField} data-key={key}>
            <label className={styles.configLabel}>{key}</label>
            {renderValue(key, value)}
          </div>
        ))}
      </div>
    </div>
  );
}
